import { Container } from "@chakra-ui/react"
import Hero from "@components/blocks/Hero"
import NormalHeading from "@components/headings/NormalHeading"
import StylizedHeading from "@components/headings/StylizedHeading"
import HeaderLayout from "@components/layout/HeaderLayout"
import { EmenuPagesTranslation } from "@definitions/types"
import { readItem } from "@directus/sdk"
import { routing } from "@i18n/routing"
import ImageOne from "@public/images/hero/pricing-image-1.jpg"
import ImageTwo from "@public/images/hero/pricing-image-2.jpg"
import ImageFour from "@public/images/hero/pricing-image-4.jpg"
import ImageFive from "@public/images/hero/pricing-image-5.png"
import ImageThreeAr from "@public/images/hero/pricing-image-guarantee-ar.png"
import ImageThreeEn from "@public/images/hero/pricing-image-guarantee-en.png"
import SvgPricing1 from "@public/svg/features/analytics.svg"
import SvgPricing3 from "@public/svg/features/devices.svg"
import SvgPricing2 from "@public/svg/features/multiple-languages.svg"
import directusClient from "@utils/directus"
import { loadMessages } from "@utils/utils"
import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next"
import dynamic from "next/dynamic"
import { useTranslations } from "next-intl"
import { NextSeo } from "next-seo"

const DynamicPackages = dynamic(
  () => import("@components/blocks/pricing/Packages"),
  { ssr: false }
)
const DynamicPackageTable = dynamic(
  () => import("@components/blocks/pricing/PackageTable"),
  { ssr: false }
)
const DynamicAddons = dynamic(
  () => import("@components/blocks/pricing/Addons"),
  { ssr: false }
)

const DynamicFooter = dynamic(() => import("@components/layout/Footer"), {
  ssr: false,
})

const Pricing: NextPage = ({
  page_data,
  locale,
  locales,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const t = useTranslations("pricing")

  return (
    <>
      <NextSeo
        title={page_data.meta_title}
        description={page_data.meta_description}
        canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/${page_data.slug}`}
        languageAlternates={locales.map((lang: string) => {
          const prefix = lang === routing.defaultLocale ? "" : `/${lang}`
          return {
            hrefLang: lang,
            href: `${process.env.NEXT_PUBLIC_SITE_URL}${prefix}/${page_data.slug}`,
          }
        })}
        additionalMetaTags={[
          {
            name: "keywords",
            content: page_data.meta_keywords,
          },
        ]}
      />
      <HeaderLayout>
        <Hero
          iconOne={SvgPricing1}
          iconTwo={SvgPricing2}
          iconThree={SvgPricing3}
          imageOne={ImageOne}
          imageTwo={ImageTwo}
          imageThree={locale === "ar" ? ImageThreeAr : ImageThreeEn}
          imageFour={ImageFour}
          imageFive={ImageFive}
          alt={t("pricing")}
        />
      </HeaderLayout>
      <Container
        maxW={"container.xl"}
        px={{ base: 10, xl: 0 }}
      >
        <NormalHeading as={"h1"}>{t("take_orders")}</NormalHeading>
        <StylizedHeading>{t("within_minutes")}</StylizedHeading>
        <DynamicPackages />
        <DynamicPackageTable />
        <DynamicAddons />
      </Container>
      <DynamicFooter />
    </>
  )
}

export default Pricing

export const getStaticProps: GetStaticProps = async ({ locale, locales }) => {
  // Page meta data
  const page_data = await directusClient
    .request(
      readItem("emenu_pages", 6, {
        fields: ["slug", { translations: ["*"] }],
      })
    )
    .then((res) => {
      const resultTranslations = res?.translations as EmenuPagesTranslation[]
      const translations = resultTranslations?.find(
        (t) => t.languages_id === locale
      )
      return {
        slug: res?.slug,
        meta_title: translations?.meta_title,
        meta_description: translations?.meta_description,
        meta_keywords: translations?.meta_keywords,
        title: translations?.title,
        content: translations?.content,
      }
    })

  const [shared, pricing] = await Promise.all([
    loadMessages("shared", locale ?? routing.defaultLocale),
    loadMessages("pricing", locale ?? routing.defaultLocale),
  ])

  return {
    props: {
      page_data,
      locale,
      locales,
      messages: { ...shared, ...pricing },
    },
  }
}
